import { isEmpty } from './isEmpty'

type SplitArrayOutput<T> = { yes: T[]; no: T[] }

export function splitArray<T>(array: T[], fxn: (t: T) => boolean): SplitArrayOutput<T> {
  const output: SplitArrayOutput<T> = { yes: [], no: [] }
  array.forEach((t) => {
    if (fxn(t)) {
      output.yes.push(t)
    } else {
      output.no.push(t)
    }
  })
  return output
}

export function unique<T extends number | string | undefined>(array: T[] | undefined): T[] {
  if (isEmpty(array)) return []
  return [...new Set(array)]
}

export function uniqueEntities<T extends object>(array: T[], getId: (item: T) => string): T[] {
  return array.filter((item, index, self) => self.findIndex((t) => getId(t) === getId(item)) === index)
}

export function sameArray(arr1: unknown[], arr2: unknown[]): boolean {
  if (arr1 === arr2) return true
  if (arr1.length !== arr2.length) return false
  const sorted1 = arr1.slice().sort()
  const sorted2 = arr2.slice().sort()
  if (sorted1.every((item, index) => item === sorted2[index])) return true
  return false
}

export function chunk<T>(array: T[], size = 25): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

export function sortByInputOrder<T, K extends string>(items: T[], orderedKeys: K[], getKey: (item: T) => K): T[] {
  const indexMap = new Map<K, number>()
  orderedKeys.forEach((key, index) => indexMap.set(key, index))
  return items.slice().sort((a, b) => {
    const indexA = indexMap.get(getKey(a)) ?? Number.MAX_SAFE_INTEGER
    const indexB = indexMap.get(getKey(b)) ?? Number.MAX_SAFE_INTEGER
    return indexA - indexB
  })
}

type ComparableKeys<T> = {
  [K in keyof T]: T[K] extends number | Date | undefined ? K : never
}[keyof T]

export function getLatestItem<T>(items: T[], key: ComparableKeys<T>): T | undefined {
  if (isEmpty(items)) return
  return items.reduce<T | undefined>((latest, item) => {
    if (!latest) return item
    return item[key] > latest[key] ? item : latest
  }, undefined)
}
